/**
 * Shared Radix UI overlay animation class strings.
 *
 * All Radix primitives (DropdownMenu, Dialog, Sheet, Popover, etc.) expose
 * `data-[state=open]` and `data-[state=closed]` attributes that Tailwind's
 * `data-*` variant targets. Centralising the class strings here ensures every
 * overlay uses identical timing and easing.
 *
 * Easing: first-party Tailwind code uses `ease-default`, `ease-exit`, and
 * `ease-position`. Directional Tailwind names remain private aliases in
 * `tokens.css` for third-party animation utilities
 * (https://animations.dev/learn/animation-theory/the-easing-blueprint).
 * For JS animations (Motion, canvas, FLIP), import the curve tuples from
 * `@/lib/ui/easing` instead of hand-rolling cubic-bezier strings.
 *
 * Usage:
 *   import { OVERLAY_ANIM, DROPDOWN_ANIM } from '@/lib/ui/animation';
 *   <DropdownMenuContent className={cn(DROPDOWN_ANIM, 'w-64')} />
 *   <DialogContent className={cn(OVERLAY_ANIM, ...)} />
 */

export {
  DURATION_ENTRANCE_MS,
  DURATION_EXIT_MS,
  DURATION_POSITION_MS,
  EASE_DEFAULT_CSS,
  EASE_DEFAULT_MOTION,
  EASE_EXIT_CSS,
  EASE_EXIT_MOTION,
  EASE_POSITION_CSS,
  EASE_POSITION_MOTION,
  MOTION_ENTRANCE,
  MOTION_EXIT,
  MOTION_POSITION,
} from './easing';

/** Fade-only — used for backdrops / overlays */
export const OVERLAY_ANIM =
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 duration-fast motion-reduce:animate-none' as const;

/**
 * Fade + scale from top-right — used for dropdown / popover panels that
 * open below-end of a trigger (e.g. wallet dropdown, header menus).
 */
export const DROPDOWN_ANIM =
  'origin-top-right data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-fast motion-reduce:animate-none' as const;

/**
 * Fade + scale from centre — used for centred modals / dialogs on desktop.
 */
export const MODAL_ANIM =
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-normal motion-reduce:animate-none' as const;

/**
 * Slide up from bottom — used for bottom-sheet panels on mobile.
 */
export const SHEET_ANIM =
  'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom duration-slow motion-reduce:animate-none' as const;
